import FormHelperText from '@mui/material/FormHelperText';
import { Theme } from '@mui/material/styles';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { ContactAddress } from '../../../sic-lib/types/ContactField';

const useStyles = makeStyles()((theme: Theme) => ({}));

type Props = Omit<
    TextFieldProps,
    'name' | 'value' | 'id' | 'label' | 'autoFocus' | 'onChange' | 'select' | 'SelectProps' | 'type'
> & {
    addressLabel: string;
    addressFields: ContactAddress;
    onChangeHandler: React.Dispatch<{ fieldValue: ContactAddress }>;
    groupHelperText?: string;
};

/**
 * TextFieldFastAddress
 *
 * @description
 * A form group containing fields matching a ContactAddress object. Prevents state updates to
 * a parent componet's state until the user has moved focus away from one of the inputs.
 *
 * @hidden
 * Useful in large forms where updates to a parent component on each keypress could cause poor
 * user experience due to slow feedback.
 *
 * @hidden
 * All props passed in which extend Mui-TextFieldProps will be applied to all inputs in the form group.
 */
export default function TextFieldFastAddress({
    addressLabel,
    addressFields,
    onChangeHandler,
    groupHelperText,
    ...props
}: Props) {
    const { classes } = useStyles();

    const [state, setState] = React.useState<ContactAddress>(addressFields);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const fieldKey = event.target.name as keyof ContactAddress;

        setState({
            ...state,
            ...{
                [fieldKey]: event.target.value,
            },
        });
    };

    const handleBlur = () => {
        onChangeHandler({ fieldValue: state });
    };

    return (
        <>
            {!!groupHelperText && (
                <FormHelperText error={!!groupHelperText}>{groupHelperText || ''}</FormHelperText>
            )}
            <TextField
                {...(props as TextFieldProps)}
                id={`${addressLabel}_street`}
                name="street"
                label="Street"
                value={state.street}
                onChange={handleChange}
                onBlur={handleBlur}
                autoFocus
            />
            <TextField
                {...(props as TextFieldProps)}
                id={`${addressLabel}_city`}
                name="city"
                label="City"
                value={state.city}
                onChange={handleChange}
                onBlur={handleBlur}
            />
            <TextField
                {...(props as TextFieldProps)}
                id={`${addressLabel}_province`}
                name="province"
                label="State/Province"
                value={state.province}
                onChange={handleChange}
                onBlur={handleBlur}
            />
            <TextField
                {...(props as TextFieldProps)}
                id={`${addressLabel}_country`}
                name="country"
                label="Country"
                value={state.country}
                onChange={handleChange}
                onBlur={handleBlur}
            />
            <TextField
                {...(props as TextFieldProps)}
                id={`${addressLabel}_postal`}
                name="postalCode"
                label="Zip/Postal Code"
                value={state.postalCode}
                onChange={handleChange}
                onBlur={handleBlur}
            />
        </>
    );
}
